import { DAY } from "./time";

/**
 * Month grids and date arithmetic for picking a range of days.
 *
 * Pure and local-time throughout. A leave day is a calendar day where the
 * worker is, not an instant — "14 September" has to mean the same thing to a
 * worker typing it and to the register printing it, which UTC parsing does not
 * guarantee once you are east of Greenwich.
 *
 * ISO strings rather than Date objects across the boundary, because that is
 * what leave_requests stores and what the screens already pass around.
 */

const pad = (n: number) => String(n).padStart(2, "0");

/** "2026-09-14" from a Date, read in local time. */
export const toISODate = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * Local-midnight Date from "2026-09-14", or null.
 *
 * NOT `new Date(s)`: that parses a bare date as UTC, so in Johannesburg it
 * lands at 02:00 on the 14th, and anywhere west of Greenwich on the 13th.
 * A day picked on a calendar must not shift under the person picking it.
 */
export function fromISODate(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(y, mo - 1, d);
  // Rejects 2026-02-31, which the constructor would roll into March.
  if (date.getFullYear() !== y || date.getMonth() !== mo - 1 || date.getDate() !== d) {
    return null;
  }
  return date;
}

export const isValidISODate = (s: string) => fromISODate(s) !== null;

/**
 * Today where the handset is, as "2026-09-14".
 *
 * NOT `new Date().toISOString().slice(0, 10)`: that is today in Greenwich, so
 * between midnight and 02:00 in Johannesburg it is still yesterday — and
 * somebody added to the household at 00:30 would start the day before.
 */
export const todayISO = (now = Date.now()) => toISODate(new Date(now));

/**
 * The last moment of a bare date, local time, or null if it is malformed.
 *
 * For "valid until" dates, which include the day they name. Parsing the date
 * the usual way gives UTC midnight at the START of it, so an ID that is good
 * until the 14th was being called expired from 02:00 on the 14th in
 * Johannesburg, and from the evening of the 13th anywhere west of Greenwich.
 */
export function endOfISODate(s: string): number | null {
  const d = fromISODate(s);
  if (!d) return null;
  // The next local midnight, less a millisecond — not +24h, which is wrong on
  // the two days a year a DST country has a 23- or 25-hour day.
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime() - 1;
}

/** Same calendar day, ignoring the time of day on either side. */
export const sameDay = (a: Date, b: Date) => toISODate(a) === toISODate(b);

/** Days from one ISO date to another, inclusive of both. 0 if either is bad
 *  or the range runs backwards — a range that ends before it starts is a
 *  mistake, not a negative number. */
export function inclusiveDays(from: string, to: string): number {
  const f = fromISODate(from);
  const t = fromISODate(to);
  if (!f || !t) return 0;
  return Math.max(0, Math.round((t.getTime() - f.getTime()) / DAY) + 1);
}

/** Whether an ISO date falls inside a closed range. Order-independent, so it
 *  is safe to call while somebody is mid-selection and has tapped the later
 *  day first. */
export function withinRange(iso: string, a: string | null, b: string | null): boolean {
  if (!a || !b) return false;
  const [lo, hi] = a <= b ? [a, b] : [b, a];
  return iso >= lo && iso <= hi;
}

export interface MonthCell {
  iso: string;
  day: number;
  /** False for the leading and trailing days that fill the first and last
   *  weeks. They are shown greyed rather than blank so the grid keeps its
   *  shape and the eye can still follow a week across a month boundary. */
  inMonth: boolean;
  weekend: boolean;
}

/**
 * Six weeks of seven days covering the given month, Monday first.
 *
 * Always six rows, so the calendar does not change height between months and
 * shove the buttons under it up and down — a picker that moves while you are
 * reaching for it is how you tap the wrong day.
 *
 * Month is 0-based, matching Date.
 */
export function monthGrid(year: number, month: number): MonthCell[][] {
  const first = new Date(year, month, 1);
  // Monday = 0. getDay() is Sunday = 0, and a week that starts on Sunday is
  // not how a South African roster is read.
  const lead = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - lead);

  const weeks: MonthCell[][] = [];
  const cursor = new Date(start);
  for (let w = 0; w < 6; w++) {
    const week: MonthCell[] = [];
    for (let d = 0; d < 7; d++) {
      const day = cursor.getDay();
      week.push({
        iso: toISODate(cursor),
        day: cursor.getDate(),
        inMonth: cursor.getMonth() === month && cursor.getFullYear() === year,
        weekend: day === 0 || day === 6,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
}

/** Step a month, carrying the year. */
export function shiftMonth(year: number, month: number, by: number) {
  const d = new Date(year, month + by, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** Monday-first headings for the grid. */
export const WEEK_HEADINGS = ["M", "T", "W", "T", "F", "S", "S"];

/** "Mon 14 Sep" — a date said the way somebody would say it out loud, which
 *  is what a field showing a chosen day should read as. */
export function fmtPickedDate(iso: string): string {
  const d = fromISODate(iso);
  if (!d) return "";
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`;
}

/**
 * "03 Sep" from a bare ISO date, in the register's style.
 *
 * The counterpart to fmtDate, which takes a timestamp. Passing a bare date
 * through Date.parse reads it as UTC, so a leave day would render as the day
 * before on any handset set west of Greenwich. Every shipped country pack is
 * currently UTC or east of it, which is the only reason that has never shown.
 */
export function fmtISODate(iso: string): string {
  const d = fromISODate(iso);
  if (!d) return "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${String(d.getDate()).padStart(2, "0")} ${months[d.getMonth()]}`;
}

/** "14 Sep – 15 Sep", or just "03 Sep" when it is a single day. A range whose
 *  ends are the same day reads as a mistake, not as one day off. */
export function fmtISORange(from: string, to: string): string {
  return from === to ? fmtISODate(from) : `${fmtISODate(from)} – ${fmtISODate(to)}`;
}
